import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logEvent } from "@/hooks/use-event-log";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { EQUIPMENT_EVENTS } from "@/lib/event-registry";

const BUCKET = "equipment-media";
const folder = (equipmentId: string) => `items/${equipmentId}`;

export interface EquipmentImage {
  path: string;
  url: string;
}

/** Bildeopplasting logges fra frontend (lagringsbøtta har ingen DB-trigger). Aktør er den ekte brukeren. */
function useLogImageEvent(equipmentId: string) {
  const { selfUser, activeUser } = useImpersonation();
  return (eventType: string, path: string) =>
    logEvent({
      event_type: eventType,
      entity_type: "equipment_items",
      entity_id: equipmentId,
      user_id: selfUser?.id,
      payload: { path, on_behalf_of: activeUser?.id !== selfUser?.id ? activeUser?.id : undefined },
    });
}

/** Egne bilder for ett utstyr i bøtta equipment-media (privat, signerte lenker). */
export function useEquipmentImages(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ["equipment-images", equipmentId],
    enabled: !!equipmentId,
    queryFn: async (): Promise<EquipmentImage[]> => {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .list(folder(equipmentId!), { sortBy: { column: "created_at", order: "asc" } });
      if (error) throw error;
      const paths = (data ?? []).filter((f) => f.id).map((f) => `${folder(equipmentId!)}/${f.name}`);
      if (paths.length === 0) return [];
      const { data: signed, error: signError } = await supabase.storage.from(BUCKET).createSignedUrls(paths, 3600);
      if (signError) throw signError;
      return (signed ?? []).filter((s) => s.signedUrl).map((s) => ({ path: s.path ?? "", url: s.signedUrl }));
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useUploadEquipmentImage(equipmentId: string) {
  const qc = useQueryClient();
  const log = useLogImageEvent(equipmentId);
  return useMutation({
    mutationFn: async (file: File) => {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${folder(equipmentId)}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type });
      if (error) throw new Error(`Opplastingen feilet: ${error.message}`);
      await log(EQUIPMENT_EVENTS.imageAdded, path);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "equipment_items", { equipmentId });
    },
  });
}

export function useDeleteEquipmentImage(equipmentId: string) {
  const qc = useQueryClient();
  const log = useLogImageEvent(equipmentId);
  return useMutation({
    mutationFn: async (path: string) => {
      const { error } = await supabase.storage.from(BUCKET).remove([path]);
      if (error) throw new Error(`Kunne ikke slette bildet: ${error.message}`);
      await log(EQUIPMENT_EVENTS.imageRemoved, path);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "equipment_items", { equipmentId });
    },
  });
}
